import useAssignNutritionist from '@/features/experts/hooks/useAssignNutritionist';
import useAssignPhysician from '@/features/experts/hooks/useAssignPhysician';
import { SortingItem } from '@/utils/api-objects/SortingItem';
import { assignSortingItems, createProduct, getProducts, getRedeemableSortingItems, redeemSortingItems } from '@/utils/apis/order-service';
import { CreateProductChannel } from '@/utils/enums/CreateProductChannel';
import { ProductType } from '@/utils/enums/ProductType';
import { useAuthStore } from '@/utils/stores/useAuthStore';
import { useExpertsStore } from '@/utils/stores/useExpertsStore';
import { useProductsStore } from '@/utils/stores/useProductsStore';
import { useUserStore } from '@/utils/stores/useUserStore';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SelectedItem {
    isToggled: boolean;
    giftEmail?: string;
    giftSaved?: boolean; // Track if gift has been properly saved
}

interface UseSortingItemsReturn {
    sortingItems: SortingItem[] | null;
    selectedItems: { [key: number]: SelectedItem };
    setSelectedItems: React.Dispatch<React.SetStateAction<{ [key: number]: SelectedItem }>>;
    isLoading: boolean;
    isFullLoading: boolean;
    error: string | null;
    handleToggle: (index: number, isToggled: boolean) => void;
    handleGiftEmailChange: (index: number, email: string) => void;
    handleGiftSaved: (index: number) => void;
    handleConfirm: () => Promise<void>;
    shouldShowExpirySessionDialog: boolean;
    setShouldShowExpirySessionDialog: (show: boolean) => void;
    isConfirmEnabled: boolean;
    dnaTestSelected: boolean;
    subSelected: boolean;
    warningMessage: string | null;
    setWarningMessage: (message: string | null) => void;
    isItemDisabled: (item: SortingItem) => boolean;
}

export const useSortingItems = (): UseSortingItemsReturn => {
    const navigate = useNavigate();
    const { auth } = useAuthStore();
    const { userData } = useUserStore();
    const { productsData } = useProductsStore();
    const email = userData.email;
    const userId = auth.userId;
    const idToken = auth.idToken;

    const { experts } = useExpertsStore();
    const physician = experts.physician
    const nutritionist = experts.nutritionist
    const { assignPhysician } = useAssignPhysician();
    const { assignNutri } = useAssignNutritionist();

    const [sortingItems, setSortingItems] = useState<SortingItem[] | null>(null);
    const [selectedItems, setSelectedItems] = useState<{ [key: number]: SelectedItem }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isFullLoading, setIsFullLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [shouldShowExpirySessionDialog, setShouldShowExpirySessionDialog] = useState(false);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);

    const fetchSortingItems = useCallback(async () => {
        if (!email || !idToken) return;

        try {
            const items = await getRedeemableSortingItems(email, idToken);
            if (items.length === 0) {
                navigate("/redeem");
                return;
            }
            setSortingItems(items);
            // Initialize selectedItems
            const initialSelectedItems: { [key: number]: SelectedItem } = items.reduce((acc, item, index) => {
                acc[index] = { 
                    isToggled: false, 
                    giftEmail: "", // Don't pre-populate with redeemable_by
                    giftSaved: false
                };
                return acc;
            }, {} as { [key: number]: SelectedItem });
            setSelectedItems(initialSelectedItems);
        } catch (error) {
            if (error instanceof Error && error.message.includes("Session expired")) {
                setShouldShowExpirySessionDialog(true);
            } else {
                setError("Error fetching sorting items");
            }
        } finally {
            setIsFullLoading(false);
        }
    }, [email, idToken, navigate]);

    useEffect(() => {
        fetchSortingItems();
        const interval = setInterval(fetchSortingItems, 60000); // Poll every minute
        return () => clearInterval(interval);
    }, [fetchSortingItems]);

    const isDnaTest = (item: SortingItem) => 
        item.product_type === ProductType.DnaTest || 
        item.product_type === ProductType.SubHealthDna || 
        item.product_type === ProductType.SubNutritionDna;

    const isSubscription = (item: SortingItem) => 
        item.product_type === ProductType.SubHealth || 
        item.product_type === ProductType.SubHealthDna || 
        item.product_type === ProductType.SubNutrition || 
        item.product_type === ProductType.SubNutritionDna ||
        item.product_type === ProductType.SubMetabolicHealth ||
        item.product_type === ProductType.SubMetabolicHealthLite;

    const handleToggle = useCallback((index: number, isToggled: boolean) => {
        if (!sortingItems) return;

        const item = sortingItems[index];
        const hasDnaTest = productsData.dnaTest !== null;
        const hasActiveMembership = productsData.membership !== null && 
                                  productsData.membership !== undefined && 
                                  productsData.membership.status === "active";

        // Only validate when toggling ON
        if (isToggled) {
            // Check if trying to toggle on a DNA test
            if (isDnaTest(item)) {
                // Check if any other DNA test is already selected
                const otherDnaTestsSelected = Object.entries(selectedItems).some(([idx, selected]) => 
                    selected.isToggled && isDnaTest(sortingItems[parseInt(idx)])
                );
                if (otherDnaTestsSelected) {
                    setWarningMessage("Puoi selezionare solo un test del DNA alla volta.");
                    return;
                }
            }

            // Check if trying to toggle on a subscription
            if (isSubscription(item)) {
                if (hasActiveMembership) {
                    setWarningMessage("Hai già un abbonamento attivo. Non puoi selezionarne un altro.");
                    return;
                }
                // Check if any other subscription is already selected
                const otherSubsSelected = Object.entries(selectedItems).some(([idx, selected]) => 
                    selected.isToggled && isSubscription(sortingItems[parseInt(idx)])
                );
                if (otherSubsSelected) {
                    setWarningMessage("Puoi selezionare solo un abbonamento alla volta.");
                    return;
                }
            }
        }

        setSelectedItems(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                isToggled,
                giftEmail: isToggled ? "" : prev[index]?.giftEmail
            }
        }));
    }, [sortingItems, selectedItems, productsData]);

    const isItemDisabled = useCallback((item: SortingItem) => {
        const hasDnaTest = productsData.dnaTest !== null && productsData.dnaTest !== undefined ;
        const hasActiveMembership = productsData.membership !== null && 
                                  productsData.membership !== undefined && 
                                  productsData.membership.status === "active";

        // If the item is already toggled, it should never be disabled
        const isItemToggled = selectedItems[sortingItems?.findIndex(i => i.id === item.id) || 0]?.isToggled;
        if (isItemToggled) return false;

        if (isDnaTest(item)) {
            if (hasDnaTest) return true;
            // Check if any other DNA test is already selected
            return Object.entries(selectedItems).some(([idx, selected]) => 
                selected.isToggled && isDnaTest(sortingItems![parseInt(idx)])
            );
        }

        if (isSubscription(item)) {
            if (hasActiveMembership) return true;
            // Check if any other subscription is already selected
            return Object.entries(selectedItems).some(([idx, selected]) => 
                selected.isToggled && isSubscription(sortingItems![parseInt(idx)])
            );
        }

        return false;
    }, [sortingItems, selectedItems, productsData]);

    const handleGiftEmailChange = useCallback((index: number, email: string) => {
        setSelectedItems(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                giftEmail: email,
                giftSaved: false // Reset saved state when email changes
            }
        }));
    }, []);

    const handleGiftSaved = useCallback((index: number) => {
        setSelectedItems(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                giftSaved: true
            }
        }));
    }, []);

    const isConfirmEnabled = sortingItems
        ? Object.entries(selectedItems).some(([index, selected]) => {
            const item = sortingItems[parseInt(index)];
            const isBoughtByMe = item.buyer === email;
            
            // Check for toggled items (self-assignment)
            if (selected.isToggled) return true;
            
            // Check for meaningful gift changes (only if gift is saved)
            if (selected.giftEmail && selected.giftEmail.trim() !== '' && selected.giftSaved) {
                // If you're the buyer and there's already a redeemable_by
                if (isBoughtByMe && item.redeemable_by) {
                    // Only meaningful if you changed the email
                    return selected.giftEmail !== item.redeemable_by;
                }
                // For other cases, any saved gift email is meaningful
                return true;
            }
            
            return false;
        })
        : false;

    const dnaTestSelected = sortingItems
        ? Object.entries(selectedItems).some(([index, selected]) => 
            selected.isToggled && isDnaTest(sortingItems[parseInt(index)])
        )
        : false;

    const subSelected = sortingItems
        ? Object.entries(selectedItems).some(([index, selected]) => 
            selected.isToggled && isSubscription(sortingItems[parseInt(index)])
        )
        : false;

    const handleConfirm = async () => {
        if (!userId || !idToken || !email || !sortingItems) return;

        const selectedItemsData = sortingItems.map((item, index) => ({
            item,
            ...selectedItems[index]
        }));

        setIsLoading(true);

        const itemsToRedeem = selectedItemsData.filter(data => data.isToggled && !data.giftEmail);
        const itemsToAssign = selectedItemsData.filter(data => data.giftEmail);

        // Prepare promises for items to assign
        const assignPromises = itemsToAssign.map((selectedItem) => {
            const gifter = selectedItem.giftEmail;
            const id = selectedItem.item.id;

            return new Promise((resolve, reject) => {
                if (!gifter) return;
                assignSortingItems(
                    id,
                    gifter,
                    idToken,
                    (success) => {
                        resolve(success);
                    },
                    (error) => {
                        console.log(error);
                        reject(error);
                    },
                    () => {
                        setShouldShowExpirySessionDialog(true);
                    },
                );
            });
        });

        // Prepare promises for items to redeem
        const redeemPromises = itemsToRedeem.map((selectedItem) => {
            return new Promise((resolve, reject) => {
                redeemSortingItems(
                    userId,
                    selectedItem.item.id,
                    email,
                    idToken,
                    (success) => {
                        const buyer = success.buyer;
                        let type = success.product_type;
                        if (type === ProductType.SubHealthDna) {
                            type = ProductType.SubHealth;
                        } else if (type === ProductType.SubNutritionDna) {
                            type = ProductType.SubNutrition;
                        }
                        const redeemer = email;
                        const shipping_address = success.product_data.shipping_address;

                        createProduct(
                            userId,
                            idToken,
                            type,
                            async (success) => {
                                if ((type === ProductType.SubMetabolicHealth || type === ProductType.SubMetabolicHealthLite || type === ProductType.MetabolicHook) && !physician) {
                                    await assignPhysician()
                                }
                                if ((type === ProductType.Nutritionist || type === ProductType.SubHealth || type === ProductType.SubNutrition || type === ProductType.SubMetabolicHealth) && !nutritionist) {
                                    await assignNutri();
                                }
                                resolve(success);
                            },
                            (error) => {
                                console.log(error);
                                reject(error);
                            },
                            {
                                shopify_product: type,
                                channel: CreateProductChannel.ShopifyPurchase,
                                code: null,
                                buyer: buyer,
                                redeemer: redeemer,
                                shipping_address: shipping_address,
                            }
                        );
                    },
                    (error) => {
                        console.log(error);
                        reject(error);
                    }
                );
            });
        });

        try {
            await Promise.all([...assignPromises, ...redeemPromises]);
            
            // Check if user has any products after successful API calls
            const products = await getProducts(userId, idToken);
            if (products && products.length > 0) {
                navigate("/fetch-data");
            } else {
                // If no products, stay on sorting page and refresh items
                await fetchSortingItems();
            }
        } catch (error) {
            console.error("Error processing items:", error);
            setError("Error processing items");
        } finally {
            setIsLoading(false);
        }
    };

    return {
        sortingItems,
        selectedItems,
        setSelectedItems,
        isLoading,
        isFullLoading,
        error,
        handleToggle,
        handleGiftEmailChange,
        handleGiftSaved,
        handleConfirm,
        shouldShowExpirySessionDialog,
        setShouldShowExpirySessionDialog,
        isConfirmEnabled,
        dnaTestSelected,
        subSelected,
        warningMessage,
        setWarningMessage,
        isItemDisabled,
    };
}; 